import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Body2 } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { createRoot } from "react-dom/client";
import { CopyButton } from "~/components/CopyToClipboard/CopyButton";
import { showSuccessToast } from "~/components/Toast";
import Angie from "~/utils/angie";
import { isInternalLink } from "~/utils/url_helper";

export const Description = ({ body, onDoubleClick, onImageClick, className }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const contentRef = useRef(null);
  const rootsRef = useRef([]);

  useEffect(() => {
    if (!contentRef.current) return;

    const preElements = contentRef.current.querySelectorAll("pre");

    preElements.forEach((pre) => {
      if (pre.querySelector(".pre-copy-button")) return;

      const wrapper = document.createElement("div");
      wrapper.style.position = "relative";
      wrapper.className = "pre-copy-wrapper";

      const buttonContainer = document.createElement("div");
      buttonContainer.className = "copy-button-container";

      pre.parentNode.insertBefore(wrapper, pre);
      wrapper.appendChild(pre);
      wrapper.appendChild(buttonContainer);

      const root = createRoot(buttonContainer);
      root.render(
        <CopyButton
          size="medium"
          onCopy={() => {
            let content = pre.innerText;
            content = content.replace(/\n\n/g, "\n\u00A0\n");
            navigator.clipboard.writeText(content);
            showSuccessToast(t("Code copied to clipboard"));
          }}
          iconClassName="pre-copy-button tw-absolute tw-top-2 tw-right-2"
        />
      );

      rootsRef.current.push(root);
    });

    return () => {
      queueMicrotask(() => {
        rootsRef.current.forEach((root) => root.unmount());
      });
      rootsRef.current = [];
    };
  }, [body, t]);

  const handleClick = useCallback(
    (event) => {
      const { tagName } = event.target;
      const attachmentId = parseInt(event.target.getAttribute(`attachment-id`));

      if (attachmentId && tagName === `IMG`) {
        event.stopPropagation();
        event.preventDefault();
        if (typeof onImageClick === "function") {
          onImageClick(attachmentId);
        }
      } else if (tagName === `A`) {
        event.preventDefault();
        event.stopPropagation();
        if (!isInternalLink(event.target.href)) {
          window.open(event.target.href, `_blank`);
        } else {
          const path = event.target.href.replace(Angie.root_url, "");
          navigate(path);
        }
      }
    },
    [navigate, onImageClick]
  );

  return (
    <Body2
      ref={contentRef}
      className={classNames("rich_text tw-break-words", className)}
      dangerouslySetInnerHTML={{ __html: body }}
      onClick={handleClick}
      onDoubleClick={onDoubleClick}
    />
  );
};

Description.propTypes = {
  body: PropTypes.string,
  onDoubleClick: PropTypes.func,
  onImageClick: PropTypes.func,
  className: PropTypes.string,
};

Description.defaultProps = {
  body: ``,
};
